import React from 'react';
import { motion } from 'framer-motion';
import { Award, TrendingUp, AlertCircle } from 'lucide-react';

const AIScoreCard = ({ score, onOptimize }) => {
  const getScoreColor = (value) => {
    if (value >= 80) return 'text-green-400';
    if (value >= 60) return 'text-yellow-400';
    return 'text-orange-400';
  };

  const getScoreGradient = (value) => {
    if (value >= 80) return 'from-green-500/20 to-emerald-500/20 border-green-500/30';
    if (value >= 60) return 'from-yellow-500/20 to-orange-500/20 border-yellow-500/30';
    return 'from-orange-500/20 to-red-500/20 border-orange-500/30';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${getScoreGradient(score.overall_score)} backdrop-blur-xl rounded-2xl border p-6`}
    >
      <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
        <Award size={20} className="text-cyan-400" />
        Score IA de qualité
      </h3>

      {/* Overall Score */}
      <div className="text-center mb-6">
        <div className={`text-5xl font-bold ${getScoreColor(score.overall_score)} mb-2`}>
          {score.overall_score}
        </div>
        <div className="text-slate-400 text-sm">Score global</div>
      </div>

      {/* Score Factors */}
      <div className="space-y-3 mb-6">
        {Object.entries(score.factors).map(([factor, value]) => (
          <div key={factor}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-300 capitalize">{factor.replace('_', ' ')}</span>
              <span className={getScoreColor(value)}>{Math.round(value)}/100</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${value}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className={`h-full ${
                  value >= 80
                    ? 'bg-gradient-to-r from-green-400 to-emerald-400'
                    : value >= 60
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-400'
                    : 'bg-gradient-to-r from-orange-400 to-red-400'
                }`}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Suggestions */}
      {score.suggestions && score.suggestions.length > 0 && (
        <div className="bg-white/5 rounded-xl p-4 mb-4">
          <h4 className="text-white font-medium text-sm mb-2 flex items-center gap-2">
            <AlertCircle size={16} className="text-cyan-400" />
            Suggestions
          </h4>
          <ul className="space-y-2">
            {score.suggestions.map((suggestion, index) => (
              <li key={index} className="text-slate-300 text-sm flex items-start gap-2">
                <span className="text-cyan-400 mt-0.5">•</span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Optimize Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onOptimize}
        className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-200"
        data-testid="optimize-button"
      >
        <TrendingUp size={18} className="inline mr-2" />
        Optimiser le contenu
      </motion.button>
    </motion.div>
  );
};

export default AIScoreCard;
